import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  intervention: null,
  asset: null,
  yields: null,
  // Project-specific data structure: { [projectId]: { ... } }
  projectData: {}
};

export const isSlice = createSlice({
  name: "is",
  initialState,
  reducers: {
    setIntervention: (state, action) => {
      state.intervention = action.payload;
    },
    setProjectProcessedData: (state, action) => {
      const { projectId, processedAssets, baselineInputs, projectInputs, emissionsMap } = action.payload;

      if (!state.projectData[projectId]) {
        state.projectData[projectId] = {
          processedAssets: {},
          baselineInputs: {},
          projectInputs: {},
          emissionsMap: {},
          totals: {
            baselineInputs: {},
            projectInputs: {},
            totalBaselineEmissions: 0,
            totalProjectEmissions: 0
          },
          interventions: {}
        };
      }

      const projectState = state.projectData[projectId];

      // Merge assets and inputs. Uploaded data overrides existing keys
      Object.assign(projectState.processedAssets, processedAssets || {});
      Object.assign(projectState.baselineInputs, baselineInputs || {});
      Object.assign(projectState.projectInputs, projectInputs || {});
      Object.assign(projectState.emissionsMap, emissionsMap || {});

      // Recompute totals from scratch
      projectState.totals = {
        baselineInputs: {},
        projectInputs: {},
        totalBaselineEmissions: 0,
        totalProjectEmissions: 0
      };

      // Aggregate emissions
      Object.values(projectState.emissionsMap).forEach((e) => {
        projectState.totals.totalBaselineEmissions += e.baseline || 0;
        projectState.totals.totalProjectEmissions += e.project || 0;
      });

      // Aggregate input totals
      Object.values(projectState.baselineInputs).forEach((arr) => {
        (arr || []).forEach((inp) => {
          const cur = projectState.totals.baselineInputs[inp.code] || 0;
          projectState.totals.baselineInputs[inp.code] = cur + (inp.application_rate || 0);
        });
      });
      Object.values(projectState.projectInputs).forEach((arr) => {
        (arr || []).forEach((inp) => {
          const cur = projectState.totals.projectInputs[inp.code] || 0;
          projectState.totals.projectInputs[inp.code] = cur + (inp.application_rate || 0);
        });
      });

      // Rebuild interventions map
      projectState.interventions = {};
      Object.entries(projectState.projectInputs).forEach(([assetId, arr]) => {
        (arr || []).forEach((inp) => {
          const mid = inp?.emission_data?.methodology_code;
          if (mid && mid !== 'NONE') {
            if (!projectState.interventions[mid]) {
              projectState.interventions[mid] = { assetIds: [], totalEmissions: 0 };
            }
            if (!projectState.interventions[mid].assetIds.includes(assetId)) {
              projectState.interventions[mid].assetIds.push(assetId);
            }
            const e = projectState.emissionsMap[assetId] || { project: 0 };
            projectState.interventions[mid].totalEmissions += e.project || 0;
          }
        });
      });
    },
    setAsset: (state, action) => {
      state.asset = action.payload;
    },
    setYield: (state, action) => {
      state.yields = action.payload;
    },
    // New project-specific reducers
    addProcessedAssetToProject: (state, action) => {
      const { projectId, asset, baselineInputs, projectInputs, emissions } = action.payload;
      
      // Initialize project data if it doesn't exist
      if (!state.projectData[projectId]) {
        state.projectData[projectId] = {
          processedAssets: {},
          baselineInputs: {},
          projectInputs: {},
          emissionsMap: {},
          totals: {
            baselineInputs: {},
            projectInputs: {},
            totalBaselineEmissions: 0,
            totalProjectEmissions: 0
          },
          interventions: {}
        };
      }
      
      const projectState = state.projectData[projectId];
      
      // Add asset data to project
      projectState.processedAssets[asset.external_asset_id] = asset;
      projectState.baselineInputs[asset.external_asset_id] = baselineInputs;
      projectState.projectInputs[asset.external_asset_id] = projectInputs;
      projectState.emissionsMap[asset.external_asset_id] = {
        baseline: emissions.baseline,
        project: emissions.project,
        unmatchedInputs: Array.from(emissions.unmatchedInputs)
      };

      // Update project totals
      projectState.totals.totalBaselineEmissions += emissions.baseline;
      projectState.totals.totalProjectEmissions += emissions.project;

      // Update baseline input totals
      baselineInputs.forEach(input => {
        const currentTotal = projectState.totals.baselineInputs[input.code] || 0;
        projectState.totals.baselineInputs[input.code] = currentTotal + (input.application_rate || 0);
      });

      // Update project input totals
      projectInputs.forEach(input => {
        const currentTotal = projectState.totals.projectInputs[input.code] || 0;
        projectState.totals.projectInputs[input.code] = currentTotal + (input.application_rate || 0);
      });

      // Update intervention-specific data
      projectInputs.forEach(input => {
        if (input.emission_data?.methodology_code && input.emission_data.methodology_code !== 'NONE') {
          if (!projectState.interventions[input.emission_data.methodology_code]) {
            projectState.interventions[input.emission_data.methodology_code] = {
              assetIds: [],
              totalEmissions: 0
            };
          }
          
          // Add asset ID if not already present
          if (!projectState.interventions[input.emission_data.methodology_code].assetIds.includes(asset.external_asset_id)) {
            projectState.interventions[input.emission_data.methodology_code].assetIds.push(asset.external_asset_id);
          }
          
          // Add emissions for this asset
          projectState.interventions[input.emission_data.methodology_code].totalEmissions += emissions.project;
        }
      });
    },
    clearProjectData: (state, action) => {
      const { projectId } = action.payload;
      if (state.projectData[projectId]) {
        delete state.projectData[projectId];
      }
    },
    clearAllProjectData: (state) => {
      state.projectData = {};
    }
  },
});

export const { 
  setAsset, 
  setIntervention, 
  setYield, 
  addProcessedAssetToProject,
  setProjectProcessedData,
  clearProjectData,
  clearAllProjectData
} = isSlice.actions;

export default isSlice.reducer;
